import {
    AddPaymentMessage,
    BusEvent,
    BusEventStatus,
    TransacitonCompleteMessage,
    ResultWrapper,
} from '@b2bsoft/kiosk-common-sdk';

import { Deferred } from '../helpers/Deferred';
import { promiseTimeout } from '../helpers/AsyncHelper';

interface KioskBusEvents {
    [key: string]: Deferred<BusMessage<any>>;
}
export interface KioskRabbitListenerOptions {
    timeout: number;
    relevantProcessId?: string;
}

export class KioskRabbitListener {
    defaultOptions: KioskRabbitListenerOptions = {
        timeout: 60 * 1000,
    };

    events: KioskBusEvents;

    constructor() {
        this.events = Object.values(BusEvent).reduce((prev, curr: string) => {
            prev[curr] = new Deferred<BusMessage<any>>();
            return prev;
        }, {} as KioskBusEvents);
    }

    onTransactionChanged(
        message: ResultWrapper<any>
    ): Promise<BusMessage<any>> {
        const busMessage = new BusMessage(message.data);
        const event = this.events[busMessage!.EventID!];

        if (!event) {
            console.error('Billpay received unknown message', BusMessage);
            return Promise.reject();
        }

        this.events[busMessage.EventID!] = new Deferred();

        if (busMessage.EventStatus === BusEventStatus.Success) {
            event.resolve(busMessage);
        } else {
            event.reject(busMessage);
        }

        return Promise.resolve(busMessage);
    }

    transactionProcessStarted(
        options: KioskRabbitListenerOptions = this.defaultOptions
    ): Promise<BusMessage<any>> {
        return promiseTimeout(
            options.timeout,
            this.events[BusEvent.TransactionProcessStarted].promise
        );
    }

    transactionCompleted(
        options: KioskRabbitListenerOptions = this.defaultOptions
    ): Promise<BusMessage<TransacitonCompleteMessage>> {
        const validateProcessRelevance = this.validateProcessRelevance(
            this.events[BusEvent.TransactionComplete].promise,
            BusEvent.TransactionComplete,
            options.relevantProcessId
        );

        return promiseTimeout(options.timeout, validateProcessRelevance);
    }

    transactionPaymentAdded(
        options: KioskRabbitListenerOptions = this.defaultOptions
    ): Promise<BusMessage<AddPaymentMessage>> {
        const validateProcessRelevance = this.validateProcessRelevance(
            this.events[BusEvent.TransactionAddPayment].promise,
            BusEvent.TransactionAddPayment,
            options.relevantProcessId
        );

        return promiseTimeout(options.timeout, validateProcessRelevance);
    }

    validateProcessRelevance(
        promise: Promise<BusMessage<any>>,
        event: BusEvent,
        processId?: string
    ): Promise<BusMessage<any>> {
        return !processId
            ? promise
            : promise.then((message) => {
                  return message.ProcessID === processId
                      ? message
                      : this.validateProcessRelevance(
                            this.events[event].promise,
                            event,
                            processId
                        );
              });
    }
}

export class BusMessage<T> {
    EventData?: T;
    EventID?: BusEvent;
    EventStatus?: BusEventStatus;
    ProcessID?: string;

    constructor(data: any) {
        Object.assign(this, data);
    }

    get transationId() {
        const data = this.EventData as any;

        return data && data.TransactionID ? Number(data.TransactionID) : null;
    }

    get isError() {
        return this.EventStatus === BusEventStatus.Error;
    }

    get errorMessage() {
        if (!this.isError) {
            return null;
        }

        const data = this.EventData as any;

        if (!data || !data.APIError) {
            return null;
        }

        try {
            const apiResponse = JSON.parse(data.APIError);

            return apiResponse.errors && Array.isArray(apiResponse.errors)
                ? apiResponse.errors.map((e: any) => e.message).join(', ')
                : apiResponse.message;
        } catch {
            // JSON could be invalid
        }

        return 'Unexpected error happened';
    }
}
